import { z } from 'zod';

export const userRoleSchema = z.enum(['student', 'parent', 'community', 'admin']);

export const pricingTierSchema = z.enum(['full', 'reduced', 'free']);

export const feeCategorySchema = z.enum(['bus_pass', 'classroom_supplies', 'field_trip', 'event_ticket', 'other']);

export const feeStatusSchema = z.enum(['pending', 'paid', 'overdue', 'cancelled']);

export const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  role: userRoleSchema,
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  studentId: z.string().uuid().optional(),
  parentId: z.string().uuid().optional(),
});

export const studentSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  grade: z.number().int().min(1).max(12),
  pricingTier: pricingTierSchema,
  parentIds: z.array(z.string().uuid()),
  schoolId: z.string().uuid(),
});

export const parentSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(10),
  studentIds: z.array(z.string().uuid()),
});

export const feeSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string(),
  category: feeCategorySchema,
  basePrice: z.number().min(0),
  reducedPrice: z.number().min(0),
  freePrice: z.number().min(0),
  availableFrom: z.date(),
  availableTo: z.date(),
  isActive: z.boolean(),
  schoolId: z.string().uuid(),
  requiresQuantity: z.boolean(),
  maxQuantity: z.number().int().positive().optional(),
});

export const preAssessedFeeSchema = z.object({
  id: z.string().uuid(),
  studentId: z.string().uuid(),
  feeId: z.string().uuid(),
  quantity: z.number().int().positive(),
  dueDate: z.date(),
  status: feeStatusSchema,
  pricingTier: pricingTierSchema,
  createdAt: z.date(),
});

export const cartItemSchema = z.object({
  id: z.string().uuid(),
  feeId: z.string().uuid(),
  fee: feeSchema,
  quantity: z.number().int().positive(),
  pricingTier: pricingTierSchema,
  unitPrice: z.number().min(0),
  totalPrice: z.number().min(0),
});

export const cartSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().optional(),
  studentId: z.string().uuid().optional(),
  items: z.array(cartItemSchema),
  subtotal: z.number().min(0),
  tax: z.number().min(0),
  total: z.number().min(0),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  role: userRoleSchema,
});

export const addToCartSchema = z.object({
  feeId: z.string().uuid(),
  quantity: z.number().int().positive(),
  studentId: z.string().uuid().optional(),
});

export type LoginFormData = z.infer<typeof loginSchema>;
export type AddToCartFormData = z.infer<typeof addToCartSchema>;
